import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BehaviorSubject, catchError, filter, first, map, mergeMap, Observable, of, skipWhile, Subject, take, takeUntil } from 'rxjs';
import { LgvClientService, LgvUserService, NotificationService } from '../../../../core/core.module';
import { LgvLoanApplicationService } from '../../../../core/services/lgv-loan-application.service';
import { LgvDataHelper } from '../../../../core/utility/lgv-data-helper';
import { LgvModel } from '../../../../data-models/longevity.model';


@Component({
    selector: 'lgv-client-picker-or-new-client',
    templateUrl: './client-picker-or-new-client.component.html',
    styleUrls: ['./client-picker-or-new-client.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default
})
export class ClientPickerOrNewClientComponent implements OnInit, OnDestroy {

    agentDTO: LgvModel.AgentDTO;
    clientInfo$ = new BehaviorSubject(null as LgvModel.ClientInfo);
    onDestroy$ = new Subject();
    isLoading$ = new BehaviorSubject(false);
    pageMode$ = new BehaviorSubject("SelectClient");

    get isSelectClientMode$(): Observable<boolean> { return (this.pageMode$.pipe(take(1), map(x=> x === 'SelectClient'))); }
    get isNewClientMode$(): Observable<boolean> { return (this.pageMode$.pipe(take(1), map(x=> x === 'NewClient'))); }

    currentAgent = '';
    currentCompany = '';
    currentOrganization = '';
    selectedClient = null;

    _form: UntypedFormGroup;

    constructor(
        private lgvClientSvc: LgvClientService,
        private fb: UntypedFormBuilder,
        public notificationSvc: NotificationService,
        public lgvDataHlpr: LgvDataHelper,
        public dialogRef: MatDialogRef<ClientPickerOrNewClientComponent>,
        public lgvUserSvc: LgvUserService,
        public matDialog: MatDialog,
        public cdr: ChangeDetectorRef,
        public lgvLoanAppSvc: LgvLoanApplicationService,
        ) {
    }


    get canSubmit$(): Observable<boolean> {return of(!!this.clientInfo$.value || this._form.valid); }
    get isFormReady$(): Observable<boolean> {return of(this._form.valid); }

    get canSubmit(): boolean {return !!this.clientInfo$.value || (this._form && this._form.valid); }
    get isFormReady(): boolean {return this._form && this._form.valid; }

    get clientModeField(): AbstractControl { return this._form.get('clientMode'); }
    get clientIdField(): AbstractControl { return this._form.get('clientId'); }
    get firstNameField(): AbstractControl { return this._form.get('firstName'); }
    get lastNameField(): AbstractControl { return this._form.get('lastName'); }
    get emailField(): AbstractControl { return this._form.get('email'); }
    get phoneField(): AbstractControl { return this._form.get('phone'); }


    ngOnInit(): void {

        this.lgvUserSvc.AgentDTO$.pipe(filter(f=> !!f), take(1)).subscribe(x=>{
            this.agentDTO = x;
        });

        this.initForm();

        this.pageMode$.pipe(takeUntil(this.onDestroy$)).subscribe(pageMode=>{
            switch(pageMode){
                case 'SelectClient':{
                    this.clientIdField.setValidators([Validators.required]);
                    this.clientIdField.setValue(null);
    
                    this.firstNameField.setValidators([]);
                    this.lastNameField.setValidators([]);
                    this.emailField.setValidators([]);
                    this.phoneField.setValidators([]);
    
                    this.firstNameField.setValue(null);
                    this.lastNameField.setValue(null);
                    this.emailField.setValue(null);
                    this.phoneField.setValue(null);
                }
                break;
                case 'NewClient':{
                    this.firstNameField.setValidators([Validators.required]);
                    this.lastNameField.setValidators([Validators.required]);
                    this.emailField.setValidators([Validators.required]);
                    this.phoneField.setValidators([Validators.required]);
    
                    this.clientIdField.clearValidators();
                    this.clientIdField.setValue(null);
                }
                break;
            }
            this.cdr?.detectChanges();
            this._form.updateValueAndValidity();        

        });

    }

    initForm() {
        this._form = this.fb.group({
            clientId: [null, [Validators.required]],
            firstName: [null],
            lastName: [null],
            email: [null],
            phone: [null]
        });

    }

    ngOnDestroy() {
        this.cdr?.detach();
        this.onDestroy$.next(true);
        this.onDestroy$.complete();
    }

    onCancel() {
        this.dialogRef.close({ action: false });
    }

    onSelectClientMode(){
        this.pageMode$.next("SelectClient");
    }

    onNewClientMode(){
        this.pageMode$.next("NewClient");
    }

    onSubmit() {

        if (this.pageMode$.value === 'NewClient') {

            this.isLoading$.next(true);

            this.lgvClientSvc.SaveClient({
                firstName: this.firstNameField.value,
                lastName: this.lastNameField.value,
                email: this.emailField.value,
                mobilePhone: this.phoneField.value,
                isPropertylessClient: true,
                ownerId: this.agentDTO.agentInfo.id,
            }).pipe(take(1), mergeMap(clientResponse => {
                return this.lgvClientSvc.LoadClient(clientResponse.id);
            })).subscribe((client) => {
                this.clientInfo$.next(client);
                this.isLoading$.next(false);
                this.dialogRef.close({ action: true, clientInfo: this.clientInfo$.value });
            },
                (err) => {
                    this.notificationSvc.error('There was an error creating this client')
                    console.log(err);
                    this.isLoading$.next(false);
                }
            );

        } else if(this.clientInfo$.value) {
            this.dialogRef.close({ action: true, clientInfo: this.clientInfo$.value });
        }
    }

    selectClient(client) {
        this.selectedClient = client;
        this.clientInfo$.next(client);
        this.clientIdField.setValue(client.id);
    }

    onClearClientSelection(){
        this.clientInfo$.next(null);
        this.clientIdField.setValue(null);
    }
}
